import React from 'react';
import { motion } from 'framer-motion';
import { Play, BarChart2, FileText, ExternalLink, BarChart } from 'lucide-react';
const clients = [{
  name: 'Siemens Healthineers',
  type: 'Interactive Poll',
  color: 'bg-blue-50 border-blue-200 text-blue-700'
}, {
  name: 'Nestlé Health Science',
  type: 'Patient Preference',
  color: 'bg-teal-50 border-teal-200 text-teal-700'
}, {
  name: 'Novartis',
  type: 'Clinical Quiz',
  color: 'bg-indigo-50 border-indigo-200 text-indigo-700'
}, {
  name: 'Roche',
  type: 'Disease Awareness',
  color: 'bg-purple-50 border-purple-200 text-purple-700'
}, {
  name: 'LEO Pharma',
  type: 'Dermatology Snak',
  color: 'bg-green-50 border-green-200 text-green-700'
}, {
  name: 'AstraZeneca',
  type: 'Treatment Journey',
  color: 'bg-violet-50 border-violet-200 text-violet-700'
}];
const formats = [{
  icon: Play,
  label: 'Video Snaks',
  desc: 'Narrative-driven micro-content'
}, {
  icon: BarChart2,
  label: 'Quiz & Polls',
  desc: 'Interactive engagement cues'
}, {
  icon: FileText,
  label: 'Impact Reports',
  desc: 'Closed-loop analytics output'
}];
export function CustomerJourneySlide() {
  return <div className="h-full flex flex-col gap-3" data-id="element-114">
      {/* Intro row — more compact */}
      <motion.p initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="text-sm text-gray-400 leading-relaxed" data-id="element-115">
        Snakker Snaks live across{' '}
        <span className="text-purple-500 font-medium" data-id="element-116">
          global pharma brands
        </span>{' '}
        — engineered for HCP attention, compliance, and measurable impact.
      </motion.p>

      {/* Main: slim left + large image */}
      <div className="flex-1 grid grid-cols-6 gap-4 min-h-0" data-id="element-117">
        {/* Left: minimal client list */}
        <div className="col-span-1 flex flex-col gap-3 justify-between py-1" data-id="element-118">
          <div className="flex flex-col gap-1.5" data-id="element-119">
            <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest mb-1" data-id="element-120">
              Clients
            </p>
            {clients.map((client, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2 + i * 0.07
          }} className="text-xs text-gray-500 font-medium py-1 border-l-2 border-gray-100 pl-2 leading-tight" data-id="element-121">
                {client.name}
                <span className="block text-[10px] text-gray-300 font-normal" data-id="element-122">
                  {client.type}
                </span>
              </motion.div>)}
          </div>

          {/* Stats — compact */}
          <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.85
        }} className="flex flex-col gap-2" data-id="element-123">
            <div className="rounded-lg bg-purple-50 border border-purple-100 px-2 py-2 text-center" data-id="element-124">
              <div className="text-lg font-bold text-purple-600" data-id="element-125">12+</div>
              <div className="text-[10px] text-gray-400" data-id="element-126">Brands</div>
            </div>
            <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-2 py-2 text-center" data-id="element-127">
              <div className="text-lg font-bold text-indigo-600" data-id="element-128">50+</div>
              <div className="text-[10px] text-gray-400" data-id="element-129">Snaks</div>
            </div>
          </motion.div>
        </div>

        {/* Right: large collage image with link overlays */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.97
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.25,
        duration: 0.5
      }} className="col-span-5 rounded-2xl border border-gray-200 overflow-hidden shadow-sm bg-white relative" data-id="element-130">
          <img src="https://cdn.magicpatterns.com/uploads/4s8hpHF8AssDMfi7XMSJBr/Screenshot_2026-02-23_at_11.29.10.png" alt="Client Snak examples: Siemens Healthineers, Nestlé Health Science, Novartis, Roche, LEO Pharma" className="w-full h-full object-contain object-center" data-id="element-131" />

          {/* Siemens — player link (bottom-left card) */}
          <a href="https://player.snakker.io/s/PjNDMqQ27Y" target="_blank" rel="noopener noreferrer" className="absolute flex items-center gap-1 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full px-2.5 py-1 text-[10px] font-semibold text-gray-700 shadow-sm hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700 transition-colors" style={{
          bottom: '18%',
          left: '3%'
        }} data-id="element-132">
            <Play className="w-2.5 h-2.5 fill-current" data-id="element-133" />
            Siemens Snak
            <ExternalLink className="w-2.5 h-2.5 opacity-50" data-id="element-134" />
          </a>

          {/* Siemens — results link (purple circle above Siemens) */}
          <a href="https://editor.snakker.io/snaks/d7337943-dff1-4a60-bd78-2038bbde8d4a/results" target="_blank" rel="noopener noreferrer" className="absolute flex items-center gap-1 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full px-2.5 py-1 text-[10px] font-semibold text-gray-700 shadow-sm hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700 transition-colors" style={{
          bottom: '38%',
          left: '3%'
        }} data-id="element-135">
            <BarChart className="w-2.5 h-2.5 text-purple-600" data-id="element-136" />
            Results
            <ExternalLink className="w-2.5 h-2.5 opacity-50" data-id="element-137" />
          </a>

          {/* Roche — player link (bottom-right card) */}
          <a href="https://player.snakker.io/s/PoLixnZjCJ" target="_blank" rel="noopener noreferrer" className="absolute flex items-center gap-1 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full px-2.5 py-1 text-[10px] font-semibold text-gray-700 shadow-sm hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700 transition-colors" style={{
          bottom: '18%',
          right: '3%'
        }} data-id="element-138">
            <Play className="w-2.5 h-2.5 fill-current" data-id="element-139" />
            Roche Snak
            <ExternalLink className="w-2.5 h-2.5 opacity-50" data-id="element-140" />
          </a>

          {/* Roche — results link (purple circle right of Roche) */}
          <a href="https://editor.snakker.io/snaks/9fd586df-9395-4715-95bd-2f0f7c49877c/results" target="_blank" rel="noopener noreferrer" className="absolute flex items-center gap-1 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full px-2.5 py-1 text-[10px] font-semibold text-gray-700 shadow-sm hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700 transition-colors" style={{
          bottom: '38%',
          right: '3%'
        }} data-id="element-141">
            <BarChart className="w-2.5 h-2.5 text-purple-600" data-id="element-142" />
            Results
            <ExternalLink className="w-2.5 h-2.5 opacity-50" data-id="element-143" />
          </a>
        </motion.div>
      </div>
    </div>;
}